import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Box,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import { useOktaAuth } from '@okta/okta-react';
import { ICreateEto } from '../models/iEto';
import ISalesOrg from '../models/iSalesOrg';
import { toast } from 'react-toastify';
import IIndexValue from '../models/iIndexValue';
import { useIndexes } from '../hooks/useIndexes';
import { useSalesOrgs } from '../hooks/useSalesOrgs';

interface IAddEtoDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (eto: ICreateEto) => Promise<boolean>;
}

export const AddEtoDialog = ({ open, onClose, onSave }: IAddEtoDialogProps) => {
  const { authState } = useOktaAuth();
  const [isSaving, setIsSaving] = useState(false);

  const accessToken = authState?.accessToken?.accessToken;

  const {
    data: salesOrgs = [],
    isLoading: salesOrgsLoading,
    error: salesOrgsError,
  } = useSalesOrgs(accessToken || '', open);
  const {
    data: productIndexes = [],
    isLoading: indexesLoading,
    error: indexesError,
  } = useIndexes(accessToken || '', open);

  const isDataLoading = salesOrgsLoading || indexesLoading;

  const [selectedSalesOrg, setSelectedSalesOrg] = useState<ISalesOrg | null>(null);
  const [productYNumber, setProductYNumber] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [catalog, setCatalog] = useState('');
  const [selectedProductIndex, setSelectedProductIndex] = useState<IIndexValue | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Handle errors
  useEffect(() => {
    if (salesOrgsError) {
      toast.error('Failed to load sales organizations.');
    }
    if (indexesError) {
      toast.error('Failed to load product indexes.');
    }
  }, [salesOrgsError, indexesError]);

  const handleClose = () => {
    setSelectedSalesOrg(null);
    setProductYNumber('');
    setProductDescription('');
    setCatalog('');
    setSelectedProductIndex(null);
    setErrors({});
    onClose();
  };

  const validate = (): boolean => {
    const tempErrors: { [key: string]: string } = {};
    if (!selectedSalesOrg) tempErrors.salesOrg = 'Sales Org is required.';
    if (!productYNumber) tempErrors.productYNumber = 'Product Y Number is required.';
    if (!selectedProductIndex) tempErrors.productIndex = 'Product Group is required.';

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const newEto: ICreateEto = {
      salesOrgId: selectedSalesOrg!.salesOrgId,
      productYNumber,
      productIndexId: selectedProductIndex!.id,
      productDescription,
      catalog,
    };

    setIsSaving(true);
    const success = await onSave(newEto);
    if (success) {
      handleClose();
    }
    setIsSaving(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Add New ETO Product</DialogTitle>
      <DialogContent>
        <Box component="form" sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid size={12}>
              <Autocomplete
                loading={isDataLoading}
                options={salesOrgs}
                getOptionLabel={(option) => option.name}
                value={selectedSalesOrg}
                onChange={(_, newValue) => setSelectedSalesOrg(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Sales Org"
                    required
                    error={!!errors.salesOrg}
                    helperText={errors.salesOrg}
                  />
                )}
                disabled={isSaving}
              />
            </Grid>
            <Grid size={12}>
              <Autocomplete
                loading={isDataLoading}
                options={productIndexes}
                getOptionLabel={(option) => option.displayName}
                value={selectedProductIndex}
                onChange={(_, newValue) => setSelectedProductIndex(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Product Group"
                    required
                    error={!!errors.productIndex}
                    helperText={errors.productIndex}
                  />
                )}
                disabled={isSaving}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                required
                label="Product Y Number"
                value={productYNumber}
                onChange={(e) => setProductYNumber(e.target.value)}
                error={!!errors.productYNumber}
                helperText={errors.productYNumber}
                disabled={isSaving}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Product Description"
                value={productDescription}
                onChange={(e) => setProductDescription(e.target.value)}
                disabled={isSaving}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Catalog"
                value={catalog}
                onChange={(e) => setCatalog(e.target.value)}
                disabled={isSaving}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={isSaving}>
          {isSaving ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
